import React from 'react';

import {Button, ButtonProps} from '@udemy-v2/react-core-components';
import {Loader} from '@udemy-v2/react-reveal-components';

import {useTransactionalButtonContext} from '@/features/course-landing-page/hooks/use-transactional-button-context';
import {useCheckoutReferrer} from 'src/lib/hooks/use-checkout-referrer';
import {OneClickCheckoutButton} from '@/components/one-click-checkout-button';
import {useClpCourse} from '../../context/clp-course-provider.react-component';

export interface ClpPrimaryCtaButtonProps extends ButtonProps {
    /** Additional CSS class names */
    className?: string;
}

export const ClpPrimaryCtaButton: React.FC<ClpPrimaryCtaButtonProps> = ({
    disabled = false,
    size = 'large',
    udStyle = 'secondary',
    ...otherButtonProps
}) => {
    const {id, frontendTrackingId} = useClpCourse();
    const courseId = Number(id);
    const {saveCheckoutReferrer} = useCheckoutReferrer();
    const {text, checkoutUrl, trackEvent, isLoading, oneClickCheckoutContext} =
        useTransactionalButtonContext();
    const isDisabled = disabled || !checkoutUrl || isLoading;

    const handleClick = () => {
        if (isDisabled) {
            return;
        }

        trackEvent();

        saveCheckoutReferrer();

        window.location.assign(checkoutUrl);
    };

    const renderButtonContent = () => {
        if (isLoading) {
            return <Loader color="inherit" overlay={true} />;
        }
        return text;
    };

    // Render OneClickCheckoutButton if enabled
    if (oneClickCheckoutContext?.isEnabled && courseId) {
        return (
            <OneClickCheckoutButton
                courseId={courseId}
                courseTrackingId={frontendTrackingId}
                size={size}
                udStyle={udStyle}
                onClick={() => {
                    trackEvent();
                    saveCheckoutReferrer();
                }}
                oneClickCheckoutContext={oneClickCheckoutContext}
                {...otherButtonProps}
            />
        );
    }

    return (
        <Button
            data-purpose="buy-now-button"
            disabled={isDisabled}
            onClick={handleClick}
            size={size}
            type="button"
            udStyle={udStyle}
            aria-busy={isLoading}
            {...otherButtonProps}
        >
            {renderButtonContent()}
        </Button>
    );
};
